/**
 * 分析(2026-09-20 発注者指示で実装。P-17)。月を選んで、いまの数字の「流れ」と「この先」を見る画面。
 *  ① 日別推移      … 取得・売上小計・粗利・原価率を日ごとに(表＋グラフ)
 *  ② 着地予想      … 3つの見方(いまのペース／直近7日のペース／目標ペース)で月末を予想する
 *  ③ 時間帯構成比  … その月の取得が何時台に何%入っているか(会社別・出勤形態別でも見る)
 *  ④ 原価率試算    … 取得・単価・人件費を ±% 動かしたときの 売上・原価・粗利・原価率
 *
 * **数字の確かめは保留(2026-09-20 発注者「分析画面は一旦実装。数字の確認は保留」)**。
 * 集計のもとは日次集計(daily_summary)で、ダッシュボード・PL と同じ `staffMetrics` / `sumAgg` を使う。
 * この画面は読むだけで、データは一切書き換えない。
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useToast } from '@core/components/Toast';
import FilterBar from '@core/components/ui/FilterBar';
import MetricCard from '@core/components/ui/MetricCard';
import PageHeader from '@core/components/ui/PageHeader';
import Panel from '@core/components/ui/Panel';
import SegmentedControl from '@core/components/ui/SegmentedControl';
import ToneValue from '@core/components/ui/ToneValue';
import { IconChevronLeft, IconChevronRight } from '@core/components/icons';
import { todayStr } from '@core/lib/date';
import { HOUR_BANDS, type Company, type DateStr, type MonthStr } from '@core/lib/types';
import { fetchCompanies } from '@core/masters/companyRepo';
import { formatCount, formatCurrency, formatPercent } from '@core/utils/formatters';
import MiniChart from './MiniChart';
import { addMonths, costRate, daysInMonth, staffMetrics, styleOf, sumAgg, type StaffMetrics } from './calc';
import { fetchMonthSummaries, fetchMonthTargets } from './dashboardRepo';
import type { MonthTargets } from '@core/lib/types';

/** 着地予想の出し方 */
type Landing = { key: string; label: string; note: string; appointments: number | null; sales: number | null; gross: number | null };

/** 時間帯構成比の切り口 */
const SPLITS = [
  { key: 'all', label: '全体' },
  { key: 'company', label: '会社別' },
  { key: 'style', label: '出勤形態別' },
] as const;
type SplitKey = (typeof SPLITS)[number]['key'];

/** 試算で動かす幅 */
const STEPS = [-20, -10, 0, 10, 20];

export default function AnalysisPage() {
  const toast = useToast();
  const [params, setParams] = useSearchParams();
  const month = (params.get('month') ?? todayStr().slice(0, 7)) as MonthStr;
  const [loading, setLoading] = useState(true);
  const [rowsByDate, setRowsByDate] = useState<{ date: DateStr; rows: StaffMetrics[] }[]>([]);
  const [targets, setTargets] = useState<MonthTargets | null>(null);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [split, setSplit] = useState<SplitKey>('all');
  /** 試算で動かす割合(%) */
  const [apptPct, setApptPct] = useState(0);
  const [pricePct, setPricePct] = useState(0);
  const [payPct, setPayPct] = useState(0);

  const setMonth = (m: MonthStr) => {
    const next = new URLSearchParams(params);
    next.set('month', m);
    setParams(next, { replace: true });
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [sums, tg, cs] = await Promise.all([fetchMonthSummaries(month), fetchMonthTargets(month), fetchCompanies()]);
      const map = new Map(cs.map((c) => [c.id, c]));
      setCompanies(cs);
      setTargets(tg);
      setRowsByDate(sums.map((s) => ({ date: s.date, rows: s.staff.map((st) => staffMetrics(st, s, tg, map)) })).sort((a, b) => a.date.localeCompare(b.date)));
    } catch (e) {
      toast(e instanceof Error ? e.message : '読み込みに失敗しました');
    } finally {
      setLoading(false);
    }
  }, [month, toast]);

  useEffect(() => {
    void load();
  }, [load]);

  /** 売上小計 = 売上見込(キャンセル分は引かない。2026-09-24 D-140。PL と同じ) */
  const netOf = (revenue: number) => revenue;

  /** ① 日別 */
  const days = useMemo(
    () =>
      rowsByDate.map(({ date, rows }) => {
        const agg = sumAgg(rows);
        const sales = netOf(agg.revenue);
        return { date, day: Number(date.slice(8)), agg, sales, gross: sales - agg.pay, rate: costRate(agg) };
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rowsByDate],
  );
  /** 実際に架電した日(取得が1件でもある日)。休みの日を平均に入れると着地予想がふくらむため */
  const worked = days.filter((d) => d.agg.appointments > 0);
  const total = useMemo(() => sumAgg(rowsByDate.flatMap((d) => d.rows)), [rowsByDate]);
  const totalSales = netOf(total.revenue);
  const totalGross = totalSales - total.pay;

  /** ② 着地予想 */
  const landings: Landing[] = useMemo(() => {
    const inMonth = daysInMonth(month);
    const done = worked.length;
    const lastDay = days.length > 0 ? days[days.length - 1]!.day : 0;
    // 「残り」は、まだ数字が入っていない暦日 × これまでの稼働率(経過した日のうち架電した日の割合)。
    // 休みの日も含めて平均を伸ばすと着地が大きく出るため、稼働率で割り引く
    const rate = lastDay > 0 ? done / lastDay : 0;
    const remain = Math.round(Math.max(0, inMonth - lastDay) * rate);
    const scale = (perDay: number | null) => (perDay === null ? null : Math.round(perDay * remain));
    const make = (key: string, label: string, note: string, list: typeof worked): Landing => {
      if (list.length === 0) return { key, label, note, appointments: null, sales: null, gross: null };
      const n = list.length;
      const ap = list.reduce((s, d) => s + d.agg.appointments, 0) / n;
      const sl = list.reduce((s, d) => s + d.sales, 0) / n;
      const gr = list.reduce((s, d) => s + d.gross, 0) / n;
      return {
        key,
        label,
        note,
        appointments: total.appointments + (scale(ap) ?? 0),
        sales: totalSales + (scale(sl) ?? 0),
        gross: totalGross + (scale(gr) ?? 0),
      };
    };
    const out = [
      make('all', 'いまのペース', `稼働した ${done}日の平均 × 残り ${remain}日(${lastDay}日まで ${done}日稼働なので、残りも同じ割合で数えます)`, worked),
      make('last7', '直近7日のペース', `直近7日の平均 × 残り ${remain}日`, worked.slice(-7)),
    ];
    // 月の取得目標は「1日の目標 × その月の日数」(月の目標は持っていないため)
    const goal = targets?.daily_appointments ? Math.round(targets.daily_appointments * inMonth) : null;
    out.push({
      key: 'goal',
      label: '目標どおり',
      note: goal ? `1日の目標 ${formatCount(targets?.daily_appointments ?? 0, { unit: '件' })} × ${inMonth}日` : '1日の取得目標が未設定です',
      appointments: goal,
      sales: null,
      gross: null,
    });
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [worked, month, total.appointments, totalSales, totalGross, targets]);

  /** ③ 時間帯構成比 */
  const hourRows = useMemo(() => {
    const all = rowsByDate.flatMap((d) => d.rows);
    const keyOf = (m: StaffMetrics) => {
      if (split === 'company') return companies.find((c) => c.id === m.company_id)?.name ?? '割振なし';
      if (split === 'style') return styleOf(m);
      return '全体';
    };
    const groups = new Map<string, number[]>();
    for (const m of all) {
      const k = keyOf(m);
      const cur = groups.get(k) ?? HOUR_BANDS.map(() => 0);
      HOUR_BANDS.forEach((_, i) => (cur[i] = (cur[i] ?? 0) + (m.by_hour[i] ?? 0)));
      groups.set(k, cur);
    }
    return [...groups.entries()]
      .map(([name, hours]) => ({ name, hours, sum: hours.reduce((s, v) => s + v, 0) }))
      .filter((g) => g.sum > 0)
      .sort((a, b) => b.sum - a.sum);
  }, [rowsByDate, split, companies]);

  /** ④ 原価率試算 */
  const sim = useMemo(() => {
    const revenue = total.revenue * (1 + apptPct / 100) * (1 + pricePct / 100);
    const sales = revenue;
    const pay = total.pay * (1 + payPct / 100);
    // 原価率の分母は **売上見込(キャンセル分を引く前)**。ダッシュボード・PL・日次【集計】と同じ数え方にそろえる
    return { sales: Math.round(sales), pay: Math.round(pay), gross: Math.round(sales - pay), rate: revenue > 0 ? (pay / revenue) * 100 : null };
  }, [total.revenue, total.pay, apptPct, pricePct, payPct]);
  const costTarget = targets?.cost_rate_total ?? null;

  const monthLabel = `${month.slice(0, 4)}年${Number(month.slice(5))}月`;
  const labels = days.map((d) => String(d.day));

  return (
    <div className="screen">
      <PageHeader title="分析" description="日別の流れ・月末の着地予想・時間帯の構成比・原価率の試算" />

      <FilterBar label="見る月">
        <span className="ui-date-nav">
          <button type="button" className="ui-btn ui-btn-secondary ui-btn-icon" onClick={() => setMonth(addMonths(month, -1) as MonthStr)} aria-label="前の月">
            <IconChevronLeft size={16} />
          </button>
          <input type="month" className="ui-input" aria-label="月" value={month} onChange={(e) => e.target.value && setMonth(e.target.value as MonthStr)} />
          <button type="button" className="ui-btn ui-btn-secondary ui-btn-icon" onClick={() => setMonth(addMonths(month, 1) as MonthStr)} aria-label="次の月">
            <IconChevronRight size={16} />
          </button>
        </span>
        <span className="ui-period-label">{monthLabel}</span>
      </FilterBar>

      {loading ? (
        <div className="ui-empty">読み込み中…</div>
      ) : days.length === 0 ? (
        <div className="ui-empty">{monthLabel}の集計データがありません</div>
      ) : (
        <>
          <div className="kpi-grid">
            <MetricCard label="取得数" value={formatCount(total.appointments)} unit="件" footnote={`稼働 ${formatCount(worked.length, { unit: '日' })}`} />
            <MetricCard label="売上小計" value={formatCurrency(totalSales)} footnote="納品 × 単価" />
            <MetricCard label="粗利" value={formatCurrency(totalGross)} footnote={`売上小計 − 人件費 ${formatCurrency(total.pay)}`} />
            <MetricCard label="原価率" value={formatPercent(costRate(total))} footnote={costTarget !== null ? `目標 ${formatPercent(costTarget)}` : undefined} />
          </div>

          {/* ---- ① 日別推移 ---- */}
          <div className="analysis-grid">
            <Panel title="取得数の日別推移" meta={monthLabel}>
              <MiniChart ariaLabel="取得数の日別推移" labels={labels} series={[{ key: 'ap', label: '取得数', values: days.map((d) => d.agg.appointments) }]} format={(v) => `${formatCount(v)}件`} />
            </Panel>
            <Panel title="原価率の日別推移" meta={costTarget !== null ? `目標 ${formatPercent(costTarget)}` : monthLabel}>
              <MiniChart
                ariaLabel="原価率の日別推移"
                labels={labels}
                series={[
                  { key: 'cr', label: '原価率', values: days.map((d) => d.rate) },
                  ...(costTarget !== null ? [{ key: 'tg', label: '目標', kind: 'target' as const, values: days.map(() => costTarget) }] : []),
                ]}
                format={(v) => formatPercent(v, { digits: 0 })}
              />
            </Panel>
          </div>

          <Panel title="日別の数字" meta={monthLabel} note="粗利は 売上小計 − 人件費です">
            <div className="report-scroll is-auto">
              <table className="report-table is-compact">
                <caption className="sr-only">日別の取得・売上・粗利・原価率</caption>
                <thead>
                  <tr>
                    <th scope="col" className="is-sticky is-sticky-edge" style={{ left: 0, width: 64 }}>日</th>
                    <th scope="col" className="is-num">取得数</th>
                    <th scope="col" className="is-num">売上小計</th>
                    <th scope="col" className="is-num">人件費</th>
                    <th scope="col" className="is-num">粗利</th>
                    <th scope="col" className="is-num">原価率</th>
                  </tr>
                </thead>
                <tbody>
                  {days.map((d) => (
                    <tr key={d.date}>
                      <th scope="row" className="is-sticky is-sticky-edge" style={{ left: 0 }}>{d.day}日</th>
                      <td className="is-num">{formatCount(d.agg.appointments)}</td>
                      <td className="is-num">{formatCurrency(d.sales)}</td>
                      <td className="is-num">{formatCurrency(d.agg.pay)}</td>
                      <td className="is-num">
                        <ToneValue tone={d.gross < 0 ? 'negative' : ''}>{formatCurrency(d.gross)}</ToneValue>
                      </td>
                      <td className="is-num">
                        <ToneValue tone={costTarget !== null && d.rate !== null && d.rate > costTarget ? 'over' : ''}>{formatPercent(d.rate)}</ToneValue>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <th scope="row" className="is-sticky is-sticky-edge" style={{ left: 0 }}>合計</th>
                    <td className="is-num">{formatCount(total.appointments)}</td>
                    <td className="is-num">{formatCurrency(totalSales)}</td>
                    <td className="is-num">{formatCurrency(total.pay)}</td>
                    <td className="is-num">{formatCurrency(totalGross)}</td>
                    <td className="is-num">{formatPercent(costRate(total))}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </Panel>

          {/* ---- ② 着地予想 ---- */}
          <Panel title="月末の着地予想" meta={monthLabel} note="「残り日数 × 1日あたりの平均」を今までの実績に足したものです。休みの予定は考えていないので、見方を3つ並べています">
            <div className="report-scroll is-auto">
              <table className="report-table">
                <caption className="sr-only">月末の着地予想</caption>
                <thead>
                  <tr>
                    <th scope="col" style={{ width: 150 }}>見方</th>
                    <th scope="col" className="is-num">取得数</th>
                    <th scope="col" className="is-num">売上小計</th>
                    <th scope="col" className="is-num">粗利</th>
                    <th scope="col">計算のしかた</th>
                  </tr>
                </thead>
                <tbody>
                  {landings.map((l) => (
                    <tr key={l.key}>
                      <th scope="row">{l.label}</th>
                      <td className="is-num">{l.appointments === null ? '—' : formatCount(l.appointments)}</td>
                      <td className="is-num">{l.sales === null ? '—' : formatCurrency(l.sales)}</td>
                      <td className="is-num">{l.gross === null ? '—' : formatCurrency(l.gross)}</td>
                      <td className="ui-hint">{l.note}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Panel>

          {/* ---- ③ 時間帯構成比 ---- */}
          <Panel
            title="時間帯の構成比"
            meta={monthLabel}
            actions={<SegmentedControl label="切り口" options={SPLITS.map((s) => ({ key: s.key, label: s.label }))} value={split} onChange={(v) => setSplit(v as SplitKey)} />}
            note="その月の取得が、何時台に何%入っているか。時間帯ごとの人の置き方を決めるときに使います"
          >
            <div className="report-scroll is-auto">
              <table className="report-table is-compact">
                <caption className="sr-only">時間帯の構成比</caption>
                <thead>
                  <tr>
                    <th scope="col" className="is-sticky is-sticky-edge" style={{ left: 0, width: 130 }}>{SPLITS.find((s) => s.key === split)?.label}</th>
                    {HOUR_BANDS.map((h) => (
                      <th key={h} scope="col" className="is-num">{h}時</th>
                    ))}
                    <th scope="col" className="is-num">合計</th>
                  </tr>
                </thead>
                <tbody>
                  {hourRows.map((g) => (
                    <tr key={g.name}>
                      <th scope="row" className="is-sticky is-sticky-edge" style={{ left: 0 }}>{g.name}</th>
                      {g.hours.map((v, i) => (
                        <td key={i} className="is-num">
                          <ToneValue tone={v === 0 ? 'muted' : ''}>{v === 0 ? '0%' : formatPercent((v / g.sum) * 100, { digits: 0 })}</ToneValue>
                        </td>
                      ))}
                      <td className="is-num">{formatCount(g.sum)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Panel>

          {/* ---- ④ 原価率試算 ---- */}
          <Panel
            title="原価率の試算"
            meta={monthLabel}
            note="いまの月の数字を基準に、取得数・会社単価・人件費を動かしたらどうなるかを見ます。**この画面だけの計算で、保存はされません**"
          >
            <div className="report-toolbar" style={{ gap: 'var(--space-4)', flexWrap: 'wrap' }}>
              {[
                { label: '取得数', value: apptPct, set: setApptPct },
                { label: '会社単価', value: pricePct, set: setPricePct },
                { label: '人件費', value: payPct, set: setPayPct },
              ].map((f) => (
                <span key={f.label} className="ui-field-inline">
                  <span className="ui-hint">{f.label}</span>
                  <SegmentedControl
                    label={`${f.label}の増減`}
                    options={STEPS.map((s) => ({ key: String(s), label: s > 0 ? `+${s}%` : `${s}%` }))}
                    value={String(f.value)}
                    onChange={(v) => f.set(Number(v))}
                  />
                </span>
              ))}
            </div>
            <div className="kpi-grid" style={{ marginTop: 'var(--space-4)' }}>
              <MetricCard label="売上小計" value={formatCurrency(sim.sales)} footnote={`いま ${formatCurrency(totalSales)}`} />
              <MetricCard label="人件費" value={formatCurrency(sim.pay)} footnote={`いま ${formatCurrency(total.pay)}`} />
              <MetricCard label="粗利" value={formatCurrency(sim.gross)} footnote={`いま ${formatCurrency(totalGross)}`} />
              <MetricCard
                label="原価率"
                value={formatPercent(sim.rate)}
                footnote={costTarget !== null ? `目標 ${formatPercent(costTarget)} ／ いま ${formatPercent(costRate(total))}` : `いま ${formatPercent(costRate(total))}`}
                valueTone={costTarget !== null && sim.rate !== null && sim.rate > costTarget ? 'danger' : undefined}
              />
            </div>
            <p className="ui-hint" style={{ marginTop: 'var(--space-3)' }}>
              取得数と会社単価は売上に、人件費は原価にそのまま掛けています(会社ごとの違いや、歩合・時給の違いは考えていません)。
              細かい前提は詰めていないので、<b>大きさの感覚をつかむ用</b>としてお使いください
            </p>
          </Panel>
        </>
      )}
    </div>
  );
}
